import React, { useState, useRef, useEffect } from 'react';
import {
  Clock, Package, Zap, FileText, Settings, File, Layout,
  ChevronDown, X
} from 'lucide-react';
import { useRecentItems, RecentItem } from '../../hooks/useRecentItems';

const iconMap = {
  Package,
  Zap,
  FileText,
  Settings,
  File,
  Layout
};

interface RecentItemsDropdownProps {
  onNavigate?: (path: string) => void;
}

export const RecentItemsDropdown: React.FC<RecentItemsDropdownProps> = ({ onNavigate }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { recentItems, clearRecentItems, removeRecentItem, getRelativeTime } = useRecentItems();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleItemClick = (item: RecentItem) => {
    if (onNavigate) {
      // Extract the section from the path if it's an admin path
      const match = item.path.match(/section=([^&]+)/);
      if (match) {
        onNavigate(match[1]);
      }
    }
    setIsOpen(false);
  };

  const handleClearAll = () => {
    if (window.confirm('Clear all recent items?')) {
      clearRecentItems();
    }
  };

  const handleRemoveItem = (e: React.MouseEvent, itemId: string) => {
    e.stopPropagation();
    removeRecentItem(itemId);
  };

  if (recentItems.length === 0) {
    return (
      <button
        disabled
        className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-400 dark:text-gray-500 rounded-lg cursor-not-allowed"
        title="No recent items"
      >
        <Clock className="w-4 h-4" />
        <span className="hidden md:inline">Recent</span>
      </button>
    );
  }

  return (
    <div ref={dropdownRef} className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
      >
        <Clock className="w-4 h-4" />
        <span className="hidden md:inline">Recent</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-50">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-gray-600 dark:text-gray-400" />
              <h3 className="font-semibold text-gray-900 dark:text-white">
                Recently Viewed
              </h3>
            </div>
            <button
              onClick={handleClearAll}
              className="text-xs text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 transition-colors"
              title="Clear all"
            >
              Clear all
            </button>
          </div>

          {/* Recent Items List */}
          <div className="max-h-96 overflow-y-auto">
            {recentItems.map((item) => {
              const Icon = iconMap[item.icon];

              return (
                <button
                  key={item.id}
                  onClick={() => handleItemClick(item)}
                  className="w-full flex items-start gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors group"
                >
                  <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg group-hover:bg-gray-200 dark:group-hover:bg-gray-600 transition-colors">
                    <Icon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                  </div>

                  <div className="flex-1 min-w-0 text-left">
                    <div className="font-medium text-gray-900 dark:text-white truncate">
                      {item.title}
                    </div>
                    {item.subtitle && (
                      <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {item.subtitle}
                      </div>
                    )}
                    <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                      {getRelativeTime(item.timestamp)}
                    </div>
                  </div>

                  <button
                    onClick={(e) => handleRemoveItem(e, item.id)}
                    className="opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-opacity"
                    title="Remove"
                  >
                    <X className="w-3 h-3 text-gray-500 dark:text-gray-400" />
                  </button>
                </button>
              );
            })}
          </div>

          {/* Footer */}
          <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
            <div className="text-xs text-gray-500 dark:text-gray-400">
              Showing {recentItems.length} recent {recentItems.length === 1 ? 'item' : 'items'}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
